import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { Control } from 'react-hook-form';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  TravelExplore as CrawlIcon,
} from '@mui/icons-material';
import {
  // ICrawlConfig,
  IOrganization,
  navStore,
  useStore,
} from '@mcn-platform/models';
import usePrompt from 'hooks/usePrompt';
import { FieldType } from 'components/forms/FieldTypes';
import ItemEdit from 'components/item/ItemEdit';
import StandardReferenceChipsField from 'components/forms/StandardReferenceChipsField';
import EditLayout from './EditLayout';

const LibraryReferenceField = (
  control: Control,
  fieldName: string,
  field:FieldType,
  readOnly: boolean,
) => (
  <StandardReferenceChipsField
    control={control}
    fieldName={fieldName}
    field={field}
    readOnly={readOnly}
  />
);

const overrides = {
  libraries: LibraryReferenceField,
};

const formConfig = [
  {
    name: 'general',
    label: 'General',
    type: 'subGroup',
    elements: [
      'name',
      'description',
      'startingUrl',
      'libraries',
      'maxPages',
      'doNotReference',
    ],
  },
  {
    name: 'includesAndExcludes',
    label: 'Includes and Excludes',
    type: 'subGroup',
    elements: [
      'includeRules',
      'excludeRules',
    ],
  },
  {
    name: 'statusGroup',
    label: 'Status',
    type: 'subGroup',
    elements: [
      'status',
      'jobID',
      'pageCount',
      'processedCount',
      'lastStatus',
    ],
  },
];

const CrawlProgressDialog = observer(({
  open,
  organization,
  crawlID,
  onClose,
} : {
  open:boolean,
  organization?:IOrganization,
  crawlID?:string,
  onClose: () => void,
}) => {
  if (!crawlID) {
    return null;
  }
  const crawlConfig = organization?.getCrawlConfig(crawlID);
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        Crawl Status:
        {' '}
        {crawlConfig?.name}
      </DialogTitle>
      <DialogContent>
        <FormControlLabel
          label="Pages Crawled"
          labelPlacement="top"
          sx={{ mx: 2 }}
          control={(
            <TextField
              value={crawlConfig?.pageCount || '-'}
              disabled
              fullWidth
            />
          )}
        />
        <FormControlLabel
          label="Pages Processed"
          labelPlacement="top"
          sx={{ mx: 2 }}
          control={(
            <TextField
              value={crawlConfig?.processedCount || '-'}
              disabled
              fullWidth
            />
          )}
        />
        <FormControlLabel
          label="Status"
          labelPlacement="top"
          sx={{ mx: 2 }}
          control={(
            <Button
              color="primary"
              variant="contained"
              fullWidth
            >
              {crawlConfig?.status}
            </Button>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const CrawlUrlsDialog = observer(({
  open,
  organization,
  crawlID,
  onClose,
  onNavigateToPage,
} : {
  open:boolean,
  organization?:IOrganization,
  crawlID?:string,
  onClose: () => void,
  onNavigateToPage?: (key: string) => void,
}) => {
  const [urls, setUrls] = React.useState('');
  const store = useStore();

  useEffect(() => {
    const showUrls = async () => {
      const response = await store.getCrawledUrls(crawlID);
      setUrls(response);
    };
    if (open && crawlID) {
      showUrls();
    }
  }, [store, crawlID, open]);

  if (!crawlID) {
    return null;
  }
  const crawlConfig = organization?.getCrawlConfig(crawlID);
  const orgID = organization?.id;
  const urlList = urls.split('\n').filter((u) => u && u !== 'URLS');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
    >
      <DialogTitle>
        Crawl URLs:
        {' '}
        {crawlConfig?.name}
      </DialogTitle>
      <DialogContent>
        {onNavigateToPage && orgID ? (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {urlList.map((url) => {
              const pageId = `${crawlID}-${encodeURIComponent(url)}`;
              const key = `${orgID}~${pageId}`;
              return (
                <ListItem key={key}>
                  <ListItemButton onClick={() => { onNavigateToPage(key); onClose(); }}>
                    <ListItemText
                      primary={url}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <TextField
            value={urls}
            fullWidth
            multiline
            rows={20}
            inputProps={{ readOnly: true }}
            sx={{ width: '100%' }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const WebCrawlConfig = () => {
  const store = useStore();
  const { crawlID } = useParams();
  const [crawlConfig, setCrawlConfig] = React.useState<any>();
  const [progressDialogOpen, setProgressDialogOpen] = React.useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = React.useState(false);
  const navigate = useNavigate();

  const { currentOrganization: organization } = store;

  usePrompt(
    {
      isDirty: organization ? (organization.changeCount > 0) : false,
      // moving between differnt configs shouldnt trigger a prompt
      pathComparator: (oldPath, newPath) => {
        console.log('oldPath is', oldPath);
        console.log('newPath is', newPath);
        return !newPath.startsWith('/app/web-crawl');
      },
    },
  );

  const saveOrganization = useCallback(() => {
    if (!organization) return Promise.reject(Error('No organization'));
    return store.update('organization', organization);
  }, [store, organization]);

  const handleSelect = (crawl:any) => {
    if (!crawl) {
      navigate('/app/web-crawl');
    } else {
      navigate(`/app/web-crawl/${crawl.id}`);
    }
  };

  const handleAdd = () => {
    const newCrawl = organization.addCrawlConfig('New Crawl');
    console.log('newCrawl is', newCrawl);
    handleSelect(newCrawl);
  };

  const handleDelete = (crawl:any) => {
    navStore.showConfirmDialog(
      'Confirm Delete',
      `Are you sure you want to delete the crawl ${crawl.name}?`,
      () => {
        organization.deleteCrawlConfig(crawl);
        handleSelect(undefined);
      },
      'Delete',
    );
  };

  const startCrawl = () => {
    if (!crawlConfig) {
      return;
    }
    navStore.showConfirmDialog(
      'Confirm Crawl',
      `Are you sure you want to start the crawl ${crawlConfig.name}?`,
      () => {
        setProgressDialogOpen(true);
        store.startCrawl(crawlConfig).then((response) => {
          console.log('response is', response);
        });
      },
      'Start Crawl',
    );
  };

  useEffect(() => {
    console.log('organization is', organization);
    if (crawlID && organization.crawlConfigs) {
      const config = organization.crawlConfigs.find((c:any) => c.id === crawlID) || undefined;
      setCrawlConfig(config);
      console.log('config is', config);
    } else {
      setCrawlConfig(undefined);
    }
  }, [store, crawlID, organization?.crawlConfigs, organization]);

  if (!organization) {
    return <div>Loading</div>;
  }

  if (organization.syncState !== 'loaded') {
    return <div>Loading.</div>;
  }

  return (
    <EditLayout
      title="Web Crawl Configuration"
      breadcrumbTitle={crawlConfig?.name?.trim() || (crawlID ? 'Crawl' : 'Web crawl')}
      buttonStack={(
        <>
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => setUrlDialogOpen(true)}
            disabled={crawlConfig?.status !== 'completed'}
          >
            Show Urls
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={startCrawl}
            disabled={!crawlConfig || organization.changeCount > 0}
          >
            Start Crawl
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={saveOrganization}
            disabled={organization.changeCount === 0}
          >
            Save
          </Button>
        </>
      )}
    >
      <Box sx={{ flex: '1', overflowY: 'auto' }}>
        <Grid container sx={{ height: '100%' }}>
          <Grid item xs={12} md={4} sx={{ height: '100%', overflow: 'hidden', pr: 2 }}>
            <Stack direction="column" spacing={1}>
              <Typography variant="h6">
                Select crawl configuration to edit
              </Typography>
              <List>
                {organization.crawlConfigs.map((c:any) => (
                  <ListItem
                    key={c.id}
                    secondaryAction={(
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(c)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  >
                    <ListItemButton onClick={() => handleSelect(c)}>
                      <ListItemAvatar>
                        <Avatar>
                          <CrawlIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={c.name}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Box>
                <Button variant="contained" onClick={handleAdd}>
                  Add Crawl
                </Button>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={8} sx={{ height: '100%', overflow: 'hidden' }}>
            {crawlConfig ? (
              <Box sx={{ flex: '1', overflowY: 'auto' }}>
                <h2>
                  {crawlConfig.name}
                </h2>
                <ItemEdit
                  key={crawlConfig.id}
                  modelName="crawlConfig"
                  item={crawlConfig}
                  formConfig={formConfig}
                  overrides={overrides}
                  // components={{
                  //   invokeAgent: <InvokeAgentField portal={portal} />,
                  // }}
                  handleSubmit={saveOrganization}
                  hideTitle
                  autoSave
                />
              </Box>
            ) : (
              <Typography>
                Please select a crawl configuration to edit
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <CrawlProgressDialog
        open={progressDialogOpen}
        organization={organization}
        crawlID={crawlID}
        onClose={() => setProgressDialogOpen(false)}
      />
      <CrawlUrlsDialog
        open={urlDialogOpen}
        organization={organization}
        crawlID={crawlID}
        onClose={() => setUrlDialogOpen(false)}
        onNavigateToPage={(key) => navigate(`/app/web-page-editor/${key}`)}
      />
    </EditLayout>
  );
};

export default observer(WebCrawlConfig);
